(ns common.dev-tools
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Fab$default" :as Fab]
            ["react-use/lib/useEvent$default" :as useEvent]
            ["react-use/lib/useWindowSize$default" :as useWindowSize]
            [common.app :as app]
            [common.db.config :as config]
            [common.dev-tools.tsq :as tsq]
            [common.mui :as mui]
            [re-frame.core :as rf]
            [re-frame.db]
            [react :as react]
            [reagent-dev-tools.core :as dev-tools]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui assemble-fixtures-view []
  (uix/use-effect (fn []
                    (rf/dispatch [:cqrs/send [:system/fixtures]]))
                  [])
  (let [{:keys [_pending value]} (use-subscribe [:cqrs/get :system/fixtures])]
    ($ :div
       ($ :strong "Fixtures")
       ($ :hr)
       ($ :table {:cellPadding "5px"}
          ($ :thead
             ($ :tr
                ($ :th "Name")
                ($ :th "Description")))
          ($ :tbody
             (for [[k {:keys [description event]}] (sort value)]
               ($ :tr {:key (str k)}
                  ($ :td {:valign "top"}
                     ($ Button
                        {:variant "contained"
                         :color "error"
                         :on-click #(rf/dispatch [:cqrs/send event])}
                        (str (pr-str k))))
                  ($ :td {:valign "top"} description))))))))

(defui mui-theme-selector []
  (let [[_update force-update] (react/useState 0)
        active (js/localStorage.getItem "theme")]
    (useEvent "themeChange" (fn [] (force-update inc)))
    ($ :div
       ($ :strong "MUI Theme")
       ($ :div
          (for [[k label] [["default" "ODS - WPP (default)"]
                           ["akqa" "ODS - AKQA"]
                           ["bcw" "ODS - BCW"]
                           ["choreograph" "ODS - Choreograph"]
                           ["essence-mediacom" "ODS - EssenceMediacom"]
                           ["groupm" "ODS - GroupM"]
                           ["landor-and-fitch" "ODS - Landor & Fitch"]
                           ["mindshare" "ODS - Mindshare"]
                           ["msix" "ODS - mSix"]
                           ["ogilvy" "ODS - Ogilvy"]
                           ["openx" "ODS - OpenX"]
                           ["vmlyr" "ODS - VMLY&R"]
                           ["wavemaker" "ODS - Wavemaker"]
                           ["wunderman-thompson" "ODS - Wunderman Thompson"]]]
            ($ Box {:key k
                    :sx #js {:my 1}}
               ($ Button
                  {:variant (if (= active k)
                              "contained"
                              "outlined")
                   :color "primary"
                   :on-click (fn [_e] (mui/select-theme! k))
                   :sx #js {:textTransform "none"}}
                  label)))))))

(defonce stored-options (atom nil))

(defn toggle-btn [open-fn]
  (let [[[delta-x delta-y] set-dragging] (react/useState nil)
        dragging (boolean delta-x)
        [[x y] set-xy] (react/useState (fn read-initial-pos []
                                         (try
                                           (js->clj (js/JSON.parse (js/localStorage.getItem "dev-tool-position")))
                                           (catch :default _
                                             nil))))
        ;; Clamp the button inside window
        ws (useWindowSize)
        el-size 46
        x (min (max 6 (or x 6)) (- (.-width ws) el-size))
        y (min (max 6 (or y 6)) (- (.-height ws) el-size))
        start-timeout (react/useRef nil)
        click-enabled (react/useRef true)
        move-callback (react/useCallback (fn [e]
                                           (set-xy [(- (.-innerWidth js/window) (.-clientX e) delta-x)
                                                    (- (.-innerHeight js/window) (.-clientY e) delta-y)]))
                                         #js [delta-x delta-y])
        up-callback (react/useCallback (fn [_e]
                                         (js/clearTimeout (.-current start-timeout))
                                         (set! (.-current start-timeout) nil)
                                         (set-dragging nil))
                                       #js [])]
    (react/useEffect (fn []
                       (js/localStorage.setItem "dev-tool-position" (js/JSON.stringify #js [x y])))
                     #js [x y])
    (react/useEffect (fn []
                       (if dragging
                         (do
                           (.addEventListener js/window "mousemove" move-callback)
                           (fn []
                             (.removeEventListener js/window "mousemove" move-callback)))
                         js/undefined))
                     #js [dragging move-callback])
    (react/useEffect (fn []
                       (.addEventListener js/window "mouseup" up-callback)
                       (fn []
                         (.removeEventListener js/window "mouseup" up-callback)))
                     #js [up-callback])
    [:> Box
     {:sx {:position "absolute"
           :zIndex 2000
           :right x
           :bottom y}}
     [:> Fab
      {:on-click (fn [_e]
                   (when (.-current click-enabled)
                     (open-fn)))
       :size "small"
       :color "primary"
       ;; Hold down for 500ms to start dragging
       :onMouseDown (fn [e]
                      (set! (.-current click-enabled) true)
                      (let [dx (- (.-innerWidth js/window) (.-clientX e) x)
                            dy (- (.-innerHeight js/window) (.-clientY e) y)
                            t (js/setTimeout (fn []
                                               (set! (.-current click-enabled) false)
                                               (set-dragging [dx dy]))
                                             500)]
                        (set! (.-current start-timeout) t)))
       :title "Hold and drag to move the button"}
      "DEV"]]))

;; This is superseded by `common.core.start_dev_tool(true)`
;; We keep ^:export here for backward compatibility, for humans.
(defn ^:export start
  "When called without options enables the DEV panel even in
  the production env.

  Application main function calls this with the application options
  and those options are stored for further use."
  ([]
   (start {:force-enable true}))
  ([{:keys [force-enable panels]}]
   (let [options (swap! stored-options merge
                        {:state-atom re-frame.db/app-db
                         :toggle-btn toggle-btn
                         :panels (into [{:key :mui
                                         :label "MUI Theme"
                                         :view [:<> ($ mui/theme-provider ($ mui-theme-selector))]}
                                        {:key :queries
                                         :label "TSQ"
                                         :view [:f> tsq/tsq-devtool]}]
                                       panels)})]
     (when (or force-enable
               (and (not (config/production-env?))
                    (not (app/running-in-playwright?))))
       (dev-tools/start! options))
     :started)))
